export interface ReceiptData {
  boletaId: string;
  fecha: string;
  productos: Array<{
    nombre: string;
    cantidad: number;
    precio: number;
  }>;
  total: number;
  pago: number;
  vuelto: number;
  printerName?: string;
}

export interface PrinterInfo {
  name: string;
  displayName: string;
  description: string;
  status: number;
  isDefault: boolean;
}

class PrintService {
  private electron: any = null;
  private fs: any = null;
  private path: any = null;
  private os: any = null;

  constructor() {
    try {
      const w: any = window as any;
      this.electron = w.electron || (w.require ? w.require('electron') : null);
      if (w.require) {
        try { this.fs = w.require('fs'); } catch {}
        try { this.path = w.require('path'); } catch {}
        try { this.os = w.require('os'); } catch {}
      }
      console.log('PrintService init', {
        hasElectron: !!this.electron,
        hasIpcRenderer: !!this.electron?.ipcRenderer,
        hasInvoke: !!this.electron?.invoke,
        preloadFlag: !!(w as any).__IS_ELECTRON__
      });
    } catch (error) {
      console.warn('Electron no disponible:', error);
    }
  }

  private diag(label: string) {
    try {
      const w: any = window as any;
      console.error('PrintService DIAG', label, {
        hasElectron: !!this.electron,
        electronKeys: this.electron ? Object.keys(this.electron) : null,
        hasIpcRenderer: !!this.electron?.ipcRenderer,
        hasInvoke: !!this.electron?.invoke,
        preloadFlag: !!w.__IS_ELECTRON__,
        userAgent: navigator?.userAgent
      });
    } catch {}
  }

  async printReceipt(receiptData: ReceiptData): Promise<{ success: boolean; message: string }> {
    if (!this.electron) {
      console.warn('Impresión no disponible en modo desarrollo web');
      return { 
        success: false, 
        message: 'Función de impresión solo disponible en la aplicación de escritorio' 
      };
    }

    try {
      const result = await this.electron.ipcRenderer.invoke('print-receipt', receiptData);
      return result;
    } catch (error) {
      console.error('Error al imprimir:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Error desconocido al imprimir' 
      };
    }
  }

  async printZReceipt(data: { caja: number, fecha: string, total: number, totalPana?: number }): Promise<{ success: boolean; message: string }> {
    try {
      // Crear iframe invisible para la impresión
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '-1000px';
      iframe.style.bottom = '-1000px';
      iframe.style.width = '300px'; // Aprox 80mm
      document.body.appendChild(iframe);

      const fmt = (v: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v);
      const fechaImpresion = new Date().toLocaleString('es-CL');
      
      const totalPana = data.totalPana || 0;
      const granTotal = data.total + totalPana;

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Reporte Z</title>
            <style>
              body { 
                font-family: 'Courier New', Courier, monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 2mm; 
                width: 76mm; 
                color: #000;
              }
              .text-center { text-align: center; }
              .header { font-weight: bold; font-size: 16px; margin-bottom: 5px; }
              .divider { border-top: 1px dashed #000; margin: 5px 0; }
              .row { display: flex; justify-content: space-between; margin-bottom: 3px; text-align: left; }
              .bold { font-weight: bold; }
              @media print {
                @page { 
                  margin: 0;
                  size: 80mm auto;
                }
                body { 
                  margin: 0;
                  padding: 2mm;
                  width: 76mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="text-center header">REPORTE Z</div>
            <div class="text-center" style="font-size: 12px; margin-bottom: 10px;">${fechaImpresion}</div>
            <div class="divider"></div>
            <div class="row"><span>Caja:</span><span>${data.caja}</span></div>
            <div class="row"><span>Fecha Caja:</span><span>${data.fecha}</span></div>
            <div class="divider"></div>
            <div class="row">
              <span>Ventas POS:</span>
              <span>${fmt(data.total)}</span>
            </div>
            ${totalPana > 0 ? `
            <div class="row">
              <span>Pagos Pana:</span>
              <span>${fmt(totalPana)}</span>
            </div>
            ` : ''}
            <div class="divider"></div>
            <div class="row bold" style="font-size: 16px;">
              <span>TOTAL GENERAL:</span>
              <span>${fmt(granTotal)}</span>
            </div>
            <div class="divider"></div>
            <div class="text-center" style="margin-top: 20px; font-size: 12px;">Generado por Sistema POS</div>
          </body>
        </html>
      `;

      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();

        // Esperar a que el contenido se cargue y lanzar print()
        setTimeout(() => {
          iframe.contentWindow?.focus();
          
          if (this.electron && this.electron.ipcRenderer) {
            // En electron forzamos el silent print si queremos, o usamos print
            iframe.contentWindow?.print();
          } else {
            iframe.contentWindow?.print();
          }

          // Remover el iframe después de un tiempo
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 3000);
        }, 500);
      } else {
        document.body.removeChild(iframe);
        throw new Error('No se pudo acceder al documento para imprimir');
      }

      // Si está en electron, también podemos notificar al main process (opcional, lo dejamos por si acaso)
      if (this.electron) {
        this.electron.ipcRenderer.invoke('print-z-receipt', data).catch(console.warn);
      }

      return { success: true, message: 'Impresión Z iniciada' };
    } catch (error) {
      console.error('Error al imprimir Z:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Error desconocido al imprimir Z' 
      };
    }
  }

  async printPanaReport(data: { fecha: string, total: number, pagos: any[] }): Promise<{ success: boolean; message: string }> {
    try {
      // Crear iframe invisible para la impresión
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '-1000px';
      iframe.style.bottom = '-1000px';
      iframe.style.width = '300px'; // Aprox 80mm
      document.body.appendChild(iframe);

      const fmt = (v: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v);
      const fechaImpresion = new Date().toLocaleString('es-CL');

   
      let detalle_lista = '<table style="width:100%; border-collapse: collapse; margin-bottom:10px;">';
      // Table headers
      detalle_lista += `
        <thead>
          <tr style="border-bottom: 1px solid #000;">
            <th style="text-align: left; padding: 2px 0;">Cliente</th>
            <th style="text-align: right; padding: 2px 0;">Monto</th>
            <th style="text-align: right; padding: 2px 0;">Fecha</th>
          </tr>
        </thead>
        <tbody>
      `;
      data.pagos.forEach((p: any) => {
        detalle_lista += `
          <tr>
            <td style="text-align: left; padding: 2px 0; max-width: 30%; word-wrap: break-word;">${p.nom_cliente}</td>
            <td style="text-align: right; padding: 2px 0;">${fmt(p.monto)}</td>
            <td style="text-align: right; padding: 2px 0; font-size: 10px;">${p.fecha_registro}</td>
          </tr>
        `;
      });
      detalle_lista += '</tbody></table>';

      const html = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Reporte Pagos Pana</title>
            <style>
              body { 
                font-family: 'Courier New', Courier, monospace; 
                font-size: 12px; 
                margin: 0; 
                padding: 2mm; 
                width: 70mm; 
                color: #000;
              }
              .text-center { text-align: center; }
              .header { font-weight: bold; font-size: 16px; margin-bottom: 5px; }
              .divider { border-top: 1px dashed #000; margin: 5px 0; }
              .row { display: flex; justify-content: space-between; margin-bottom: 3px; text-align: left; }
              .bold { font-weight: bold; }
              @media print {
                @page { 
                  margin: 0;
                  size: 80mm auto;
                }
                body { 
                  margin: 0;
                  padding: 2mm;
                  width: 70mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="text-center header">PAGOS PANA (POS)</div>
            <div class="text-center" style="font-size: 12px; margin-bottom: 10px;">${fechaImpresion}</div>
            <div class="divider"></div>
            <div class="row"><span>Fecha Consultada:</span><span>${data.fecha}</span></div>
            <div class="divider"></div>
            ${detalle_lista}
            <div class="divider"></div>
            <div class="row bold" style="font-size: 16px;">
              <span>Total Recaudado:</span>
              <span>${fmt(data.total)}</span>
            </div>
            <div class="divider"></div>
            <div class="text-center" style="margin-top: 20px; font-size: 12px;">Generado por Sistema POS</div>
          </body>
        </html>
      `;

      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();

        // Esperar a que el contenido se cargue y lanzar print()
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // Remover el iframe después de un tiempo
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 3000);
        }, 500);
      } else {
        document.body.removeChild(iframe);
        throw new Error('No se pudo acceder al documento para imprimir');
      }

      return { success: true, message: 'Impresión iniciada' };
    } catch (error) {
      console.error('Error al imprimir:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Error desconocido al imprimir' 
      };
    }
  }

  async getPrinters(): Promise<PrinterInfo[]> {
    if (!this.electron) {
      // Retornar impresoras de prueba para desarrollo
      return [
        {
          name: 'Microsoft Print to PDF',
          displayName: 'Microsoft Print to PDF',
          description: 'Impresora PDF virtual',
          status: 0,
          isDefault: true
        },
        {
          name: 'EPSON TM-T20III',
          displayName: 'EPSON TM-T20III',
          description: 'Impresora térmica',
          status: 0,
          isDefault: false
        }
      ];
    }

    try {
      const printers = await this.electron.ipcRenderer.invoke('get-printers');
      return printers.map((printer: any) => ({
        name: printer.name,
        displayName: printer.displayName || printer.name,
        description: printer.description || '',
        status: printer.status || 0,
        isDefault: printer.isDefault || false
      }));
    } catch (error) {
      console.error('Error al obtener impresoras:', error);
      return [];
    }
  }

  async printTestPage(): Promise<{ success: boolean; message: string }> {
    const testData: ReceiptData = {
      boletaId: 'TEST-001',
      fecha: new Date().toLocaleString('es-CL'),
      productos: [
        { nombre: 'Producto de Prueba 1', cantidad: 2, precio: 1500 },
        { nombre: 'Producto de Prueba 2', cantidad: 1, precio: 2500 }
      ],
      total: 5500,
      pago: 10000,
      vuelto: 4500
    };

    return await this.printReceipt(testData);
  }

  async printPdf(filePath: string, printerName?: string, copies: number = 1): Promise<{ success: boolean; message: string }>{  
    console.log('printService.printPdf', { filePath, printerName, copies, electron: !!this.electron });
    if (!this.electron || !this.electron.ipcRenderer?.invoke) {
      this.diag('printPdf-noElectron');
      return { success: false, message: 'Solo disponible en la app de escritorio' };
    }
    try {
      const result = await this.electron.ipcRenderer.invoke('print-pdf', { filePath, printerName, copies });
      return result;
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Error al imprimir PDF' };
    }
  }

  async saveBase64Pdf(base64: string, fileName?: string): Promise<string> {
    if (!this.fs || !this.path || !this.os) throw new Error('Entorno Electron requerido');
    const safeName = (fileName || `documento_${Date.now()}`).replace(/[^a-zA-Z0-9._-]/g, '_');
    const outPath = this.path.join(this.os.tmpdir(), `${safeName}.pdf`);
    const buffer = Buffer.from(base64, 'base64');
    await this.fs.promises.writeFile(outPath, buffer);
    return outPath;
  }

  private downloadBase64PdfWeb(base64: string, fileName?: string): { success: boolean; message: string } {
    try {
      const safeName = (fileName || `documento_${Date.now()}`).replace(/[^a-zA-Z0-9._-]/g, '_') + '.pdf';
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { success: true, message: 'PDF descargado' };
    } catch (e: any) {
      return { success: false, message: e?.message || 'Error al descargar PDF' };
    }
  }

  async printDtePdf(dte: any, printerName?: string): Promise<{ success: boolean; message: string }>{
    try {
      const folio = dte?.folio || dte?.FOLIO || dte?.archivos?.FOLIO;
      const base64 = dte?.response?.PDF || dte?.PDF;
      if (base64) {
        console.log('printService.printDtePdf base64', { folio, hasBase64: !!base64 });
        if (this.electron) {
          const filePath = await this.saveBase64Pdf(base64, `boleta_${folio || 'sin_folio'}`);
          return await this.printPdf(filePath, printerName);
        } else {
          return this.downloadBase64PdfWeb(base64, `boleta_${folio || 'sin_folio'}`);
        }
      }
      const pathFromBackend = dte?.archivos?.PDF;
      if (pathFromBackend) {
        console.log('printService.printDtePdf path', { folio, pathFromBackend });
        if (this.electron) {
          return await this.printPdf(pathFromBackend, printerName);
        } else {
          const a = document.createElement('a');
          a.href = pathFromBackend;
          a.target = '_blank';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return { success: true, message: 'PDF abierto' };
        }
      }
      return { success: false, message: 'No se encontró PDF en la respuesta DTE' };
    } catch (error) {
      return { success: false, message: error instanceof Error ? error.message : 'Error procesando DTE PDF' };
    }
  }

  async saveDtePdfToApp(dte: any, fileName?: string): Promise<{ success: boolean; message: string; path?: string }>{
    const folio = dte?.folio || dte?.FOLIO || dte?.archivos?.FOLIO;
    const base64 = dte?.response?.PDF || dte?.PDF;
    const name = fileName || `boleta_${folio || 'sin_folio'}`;
    console.log('saveDtePdfToApp', { folio, base64,name });
    try {
      console.log('printService.saveDtePdfToApp', { folio, hasBase64: !!base64, fileName: name, electron: !!this.electron });
      if (!this.electron || !this.electron.ipcRenderer?.invoke) {
        this.diag('saveDtePdfToApp-noElectron');
        // En web, descargar el PDF si hay base64, o abrir ruta si existe
        if (base64) {
          const res = this.downloadBase64PdfWeb(base64, name);
          return { ...res, path: undefined } as any;
        }
        if (dte?.archivos?.PDF) {
          const a = document.createElement('a');
          a.href = dte.archivos.PDF;
          a.target = '_blank';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return { success: true, message: 'PDF abierto' };
        }
        return { success: false, message: 'No hay PDF para guardar' };
      }
      const resp = await this.electron.ipcRenderer.invoke('save-pdf', { base64, fileName: name });
      return resp;
    } catch (e: any) {
      this.diag('saveDtePdfToApp-catch');
      return { success: false, message: e?.message || 'Error guardando PDF' };
    }
  }
}

export const printService = new PrintService();
export default printService;
